import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { marketplaceApi, type MarketplaceApp } from '../api/client'
import { Badge, PageHeader, Skeleton } from '../components/ui'

function formatPrice(app: MarketplaceApp, t: (k: string, opts?: Record<string, unknown>) => string) {
  if (app.price_cents === 0) return t('marketplace.priceFree')
  const formatted = (app.price_cents / 100).toFixed(0)
  return t('marketplace.priceOneTime', { price: formatted, currency: app.currency })
}

function KindBadge({ kind }: { kind: MarketplaceApp['kind'] }) {
  const { t } = useTranslation()
  // Downloadable means "you install it yourself" — a caveat, hence the warning
  // tone; installable is the ordinary path.
  return kind === 'downloadable' ? (
    <Badge tone="warning">{t('marketplace.kindDownloadable')}</Badge>
  ) : (
    <Badge tone="accent">{t('marketplace.kindInstallable')}</Badge>
  )
}

export default function MarketplacePage() {
  const { t } = useTranslation()
  const { data: apps, isLoading } = useQuery({
    queryKey: ['marketplace-apps'],
    queryFn: marketplaceApi.list,
    staleTime: 60_000,
  })

  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
      <PageHeader title={t('marketplace.title')} description={t('marketplace.subtitle')} className="mb-8" />

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(apps ?? []).map((app) => (
            <Link
              key={app.id}
              to={`/apps/${app.slug}`}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:border-brand-400 hover:shadow-sm transition-all flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-3xl shrink-0">{app.icon || '📦'}</div>
                <div className="flex flex-col items-end gap-1">
                  <KindBadge kind={app.kind} />
                  <span
                    className={`text-xs font-semibold ${
                      app.price_cents === 0 ? 'text-emerald-600' : 'text-gray-700'
                    }`}
                  >
                    {formatPrice(app, t)}
                  </span>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{app.name}</h3>
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{app.tagline}</p>
              </div>
              <p className="text-xs text-gray-400 mt-auto">v{app.version}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
